import { c as _c } from "react/compiler-runtime";
import { getOrgAgentsMcpServersOptions, getOrgAgentsMcpServersQueryKey, postOrgAgentsMcpServersByServerIdOauthStartMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { IcoDotsHorizontal16, IcoPlusSmall16, IcoServer16 } from '@onefootprint/icons';
import type { AgentMcpServer, AgentTool } from '@onefootprint/request-types/dashboard';
import { Badge, Button, Card, Dropdown, IconButton, Shimmer, Toggle, useToast } from '@onefootprint/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ErrorComponent from '@/components/error';
import useBuilderNavigation from '../../hooks/use-builder-navigation';
import { useBuilder } from '../../store/provider';
import { isMcpTool } from '../../store/types';
import ToolFilters, { activeToolFilters } from '../tool-filters';
import AddMcpServerDialog from './components/add-mcp-server-dialog';
import ReplaceHeaderValueDialog from './components/replace-header-value-dialog';
import useMcpOauthPopup, { mcpOauthRedirectUri } from './use-mcp-oauth-popup';
const McpView = () => {
  const $ = _c(85);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "agentBuilder.tools"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t0);
  const {
    state,
    actions,
    isReadOnly,
    isSavingGraph
  } = useBuilder();
  const {
    mcpFilter: filter,
    mcpQuery: query,
    setMcpFilter,
    setMcpQuery
  } = useBuilderNavigation();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [replacing, setReplacing] = useState(null);
  const queryClient = useQueryClient();
  const {
    toast
  } = useToast();
  const showErrorToast = useRequestErrorToast();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getOrgAgentsMcpServersOptions();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const mcpQuery = useQuery(t1);
  let t2;
  if ($[2] !== queryClient || $[3] !== t || $[4] !== toast) {
    t2 = {
      onComplete: async server => {
        await queryClient.invalidateQueries({
          queryKey: getOrgAgentsMcpServersQueryKey()
        });
        toast({
          variant: "default",
          title: t(_temp, {
            name: server.name
          }),
          description: ""
        });
      }
    };
    $[2] = queryClient;
    $[3] = t;
    $[4] = toast;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const reconnectPopup = useMcpOauthPopup(t2);
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = postOrgAgentsMcpServersByServerIdOauthStartMutation();
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== reconnectPopup || $[8] !== t || $[9] !== toast) {
    t4 = t5 => {
      const {
        authorizationUrl
      } = t5;
      if (reconnectPopup.open(authorizationUrl)) {
        return;
      }
      toast({
        variant: "error",
        title: t(_temp2),
        description: ""
      });
    };
    $[7] = reconnectPopup;
    $[8] = t;
    $[9] = toast;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== showErrorToast || $[12] !== t4) {
    t5 = {
      ...t3,
      onSuccess: t4,
      onError: showErrorToast
    };
    $[11] = showErrorToast;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const reconnectMutation = useMutation(t5);
  let t6;
  if ($[14] !== state.bundle.tools) {
    t6 = new Set(state.bundle.tools.filter(isMcpTool).map(_temp3));
    $[14] = state.bundle.tools;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const selectedServerIds = t6;
  let T0;
  let handleCreated;
  let t10;
  let t11;
  let t12;
  let t7;
  let t8;
  let t9;
  if ($[16] !== actions || $[17] !== filter || $[18] !== isReadOnly || $[19] !== isSavingGraph || $[20] !== mcpQuery.data || $[21] !== mcpQuery.error || $[22] !== mcpQuery.isPending || $[23] !== query || $[24] !== reconnectMutation || $[25] !== selectedServerIds || $[26] !== setMcpFilter || $[27] !== setMcpQuery || $[28] !== state.bundle.tools || $[29] !== t) {
    const servers = mcpQuery.data ?? [];
    let t13;
    if ($[38] !== selectedServerIds) {
      t13 = server_0 => selectedServerIds.has(server_0.id);
      $[38] = selectedServerIds;
      $[39] = t13;
    } else {
      t13 = $[39];
    }
    const enabledCount = servers.filter(t13).length;
    const active = activeToolFilters(servers.length, filter, query);
    const needle = active.needle;
    const filteredServers = servers.filter(server_1 => active.filter === "all" || selectedServerIds.has(server_1.id) === (active.filter === "enabled")).filter(server_2 => !needle || `${server_2.name} ${server_2.url}`.toLowerCase().includes(needle));
    let t14;
    if ($[40] !== actions || $[41] !== state.bundle.tools) {
      t14 = (serverId, enabled) => {
        const next = enabled ? [...state.bundle.tools, {
          kind: "mcp",
          data: {
            serverId
          }
        }] : state.bundle.tools.filter(tool_0 => !isMcpTool(tool_0) || tool_0.data.serverId !== serverId);
        actions.setTools(next);
      };
      $[40] = actions;
      $[41] = state.bundle.tools;
      $[42] = t14;
    } else {
      t14 = $[42];
    }
    const toggleServer = t14;
    let t15;
    if ($[43] !== toggleServer) {
      t15 = server_3 => {
        setIsAddOpen(false);
        toggleServer(server_3.id, true);
      };
      $[43] = toggleServer;
      $[44] = t15;
    } else {
      t15 = $[44];
    }
    handleCreated = t15;
    let t16;
    if ($[45] !== reconnectMutation) {
      t16 = server_4 => {
        reconnectMutation.mutate({
          path: {
            serverId: server_4.id
          },
          body: {
            redirectUri: mcpOauthRedirectUri()
          }
        });
      };
      $[45] = reconnectMutation;
      $[46] = t16;
    } else {
      t16 = $[46];
    }
    const handleReconnect = t16;
    const isEmpty = !mcpQuery.isPending && !mcpQuery.error && !mcpQuery.data?.length;
    const renderServers = () => {
      if (mcpQuery.isPending) {
        return <div role="status" aria-label={t(_temp4)} className="flex flex-col gap-1">{[0, 1].map(_temp5)}</div>;
      }
      if (mcpQuery.error) {
        return <Card.Body className="px-4 py-3"><ErrorComponent error={mcpQuery.error} /></Card.Body>;
      }
      if (isEmpty) {
        return <Card.Empty>{t(_temp6)}</Card.Empty>;
      }
      if (filteredServers.length === 0) {
        const message = needle ? t(_temp7) : active.filter === "enabled" ? t(_temp8) : t(_temp9);
        return <Card.Empty role="status">{message}</Card.Empty>;
      }
      return <Card.List aria-label={t(_temp10)}>{filteredServers.map(server_5 => <Card.Body as="li" key={server_5.id} className="flex items-center gap-3 px-4 py-3"><span aria-hidden={true} className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-tertiary bg-secondary text-secondary"><IcoServer16 /></span><div className="min-w-0 flex-1"><div className="flex flex-wrap items-center gap-2"><p className="text-label-3 text-primary break-words">{server_5.name}</p>{server_5.authKind === "oauth" && <Badge variant={server_5.oauthError ? "error" : "neutral"} title={server_5.oauthError}>{server_5.oauthError ? t(_temp11) : t(_temp12)}</Badge>}</div><p className="mt-1 text-body-3 text-tertiary truncate" title={server_5.url}>{server_5.url}</p></div><Toggle aria-label={server_5.name} checked={selectedServerIds.has(server_5.id)} disabled={isReadOnly || isSavingGraph} onChange={value => toggleServer(server_5.id, value)} />{!isReadOnly && <ServerMenu server={server_5} disabled={isSavingGraph || reconnectMutation.isPending} onReplaceHeader={() => setReplacing(server_5)} onReconnect={() => handleReconnect(server_5)} />}</Card.Body>)}</Card.List>;
    };
    t11 = "max-w-[800px] mx-auto px-6 py-8 sm:px-10 flex flex-col gap-8";
    let t17;
    if ($[47] !== t) {
      t17 = t(_temp13);
      $[47] = t;
      $[48] = t17;
    } else {
      t17 = $[48];
    }
    let t18;
    if ($[49] !== t17) {
      t18 = <h2 className="text-heading-2 text-primary">{t17}</h2>;
      $[49] = t17;
      $[50] = t18;
    } else {
      t18 = $[50];
    }
    let t19;
    if ($[51] !== t) {
      t19 = t(_temp14);
      $[51] = t;
      $[52] = t19;
    } else {
      t19 = $[52];
    }
    let t20;
    if ($[53] !== t19) {
      t20 = <p className="text-body-2 text-tertiary">{t19}</p>;
      $[53] = t19;
      $[54] = t20;
    } else {
      t20 = $[54];
    }
    if ($[55] !== t18 || $[56] !== t20) {
      t12 = <div className="flex flex-col gap-1">{t18}{t20}</div>;
      $[55] = t18;
      $[56] = t20;
      $[57] = t12;
    } else {
      t12 = $[57];
    }
    t9 = "@container flex flex-col gap-6";
    t10 = active.canFilter && <ToolFilters filter={filter} query={query} enabledCount={enabledCount} onFilterChange={setMcpFilter} onQueryChange={setMcpQuery} label={t(_temp15)} searchLabel={t(_temp16)} />;
    t8 = "flex flex-col items-center gap-2";
    T0 = Card.Root;
    t7 = renderServers();
    $[16] = actions;
    $[17] = filter;
    $[18] = isReadOnly;
    $[19] = isSavingGraph;
    $[20] = mcpQuery.data;
    $[21] = mcpQuery.error;
    $[22] = mcpQuery.isPending;
    $[23] = query;
    $[24] = reconnectMutation;
    $[25] = selectedServerIds;
    $[26] = setMcpFilter;
    $[27] = setMcpQuery;
    $[28] = state.bundle.tools;
    $[29] = t;
    $[30] = T0;
    $[31] = handleCreated;
    $[32] = t10;
    $[33] = t11;
    $[34] = t12;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
  } else {
    T0 = $[30];
    handleCreated = $[31];
    t10 = $[32];
    t11 = $[33];
    t12 = $[34];
    t7 = $[35];
    t8 = $[36];
    t9 = $[37];
  }
  let t13;
  if ($[58] !== T0 || $[59] !== t7) {
    t13 = <T0>{t7}</T0>;
    $[58] = T0;
    $[59] = t7;
    $[60] = t13;
  } else {
    t13 = $[60];
  }
  let t14;
  if ($[61] !== isReadOnly || $[62] !== isSavingGraph || $[63] !== t) {
    t14 = !isReadOnly && <Button variant="secondary" size="compact" prefixIcon={IcoPlusSmall16} disabled={isSavingGraph} onClick={() => setIsAddOpen(true)}>{t(_temp17)}</Button>;
    $[61] = isReadOnly;
    $[62] = isSavingGraph;
    $[63] = t;
    $[64] = t14;
  } else {
    t14 = $[64];
  }
  let t15;
  if ($[65] !== t13 || $[66] !== t14 || $[67] !== t8) {
    t15 = <div className={t8}>{t13}{t14}</div>;
    $[65] = t13;
    $[66] = t14;
    $[67] = t8;
    $[68] = t15;
  } else {
    t15 = $[68];
  }
  let t16;
  if ($[69] !== t10 || $[70] !== t15 || $[71] !== t9) {
    t16 = <section className={t9}>{t10}{t15}</section>;
    $[69] = t10;
    $[70] = t15;
    $[71] = t9;
    $[72] = t16;
  } else {
    t16 = $[72];
  }
  let t17;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setIsAddOpen(false);
    $[73] = t17;
  } else {
    t17 = $[73];
  }
  let t18;
  if ($[74] !== handleCreated || $[75] !== isAddOpen) {
    t18 = <AddMcpServerDialog open={isAddOpen} onClose={t17} onCreated={handleCreated} />;
    $[74] = handleCreated;
    $[75] = isAddOpen;
    $[76] = t18;
  } else {
    t18 = $[76];
  }
  let t19;
  if ($[77] !== replacing) {
    t19 = replacing && <ReplaceHeaderValueDialog server={replacing} onClose={() => setReplacing(null)} />;
    $[77] = replacing;
    $[78] = t19;
  } else {
    t19 = $[78];
  }
  let t20;
  if ($[79] !== t11 || $[80] !== t12 || $[81] !== t16 || $[82] !== t18 || $[83] !== t19) {
    t20 = <div className={t11}>{t12}{t16}{t18}{t19}</div>;
    $[79] = t11;
    $[80] = t12;
    $[81] = t16;
    $[82] = t18;
    $[83] = t19;
    $[84] = t20;
  } else {
    t20 = $[84];
  }
  return t20;
};
type ServerMenuProps = {
  server: AgentMcpServer;
  disabled: boolean;
  onReplaceHeader: () => void;
  onReconnect: () => void;
};
const ServerMenu = t0 => {
  const $ = _c(17);
  const {
    server,
    disabled,
    onReplaceHeader,
    onReconnect
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.tools.mcp.actions"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const [open, setOpen] = useState(false);
  let t2;
  if ($[1] !== server.name || $[2] !== t) {
    t2 = t(_temp18, {
      name: server.name
    });
    $[1] = server.name;
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = open ? "bg-senary/70!" : undefined;
  let t4;
  if ($[4] !== disabled || $[5] !== t2 || $[6] !== t3) {
    t4 = <Dropdown.Trigger asChild={true}><IconButton aria-label={t2} size="compact" icon={IcoDotsHorizontal16} className={t3} disabled={disabled} /></Dropdown.Trigger>;
    $[4] = disabled;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onReconnect || $[9] !== onReplaceHeader || $[10] !== server.authKind || $[11] !== t) {
    t5 = <Dropdown.Portal><Dropdown.Content align="end"><Dropdown.Group>{server.authKind === "oauth" ? <Dropdown.Item onSelect={onReconnect}>{t(_temp19)}</Dropdown.Item> : <Dropdown.Item onSelect={onReplaceHeader}>{t(_temp20)}</Dropdown.Item>}</Dropdown.Group></Dropdown.Content></Dropdown.Portal>;
    $[8] = onReconnect;
    $[9] = onReplaceHeader;
    $[10] = server.authKind;
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== open || $[14] !== t4 || $[15] !== t5) {
    t6 = <Dropdown.Root open={open} onOpenChange={setOpen}>{t4}{t5}</Dropdown.Root>;
    $[13] = open;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};
export default McpView;
function _temp($) {
  return $.mcp.reconnect.done;
}
function _temp2($_0) {
  return $_0.mcp.reconnect.popupBlocked;
}
function _temp3(tool) {
  return tool.data.serverId;
}
function _temp4($_1) {
  return $_1.mcp.loading;
}
function _temp5(index) {
  return <Card.Body key={index} className="px-4 py-3"><Shimmer height="40px" width="100%" /></Card.Body>;
}
function _temp6($_2) {
  return $_2.mcp.emptyState;
}
function _temp7($_3) {
  return $_3.mcp.noMatches;
}
function _temp8($_4) {
  return $_4.mcp.noneEnabled;
}
function _temp9($_5) {
  return $_5.mcp.allEnabled;
}
function _temp10($_6) {
  return $_6.mcp.title;
}
function _temp11($_7) {
  return $_7.mcp.needsReconnect;
}
function _temp12($_8) {
  return $_8.mcp.oauthBadge;
}
function _temp13($_9) {
  return $_9.mcp.title;
}
function _temp14($_10) {
  return $_10.mcp.subtitle;
}
function _temp15($_11) {
  return $_11.mcp.filterLabel;
}
function _temp16($_12) {
  return $_12.mcp.search;
}
function _temp17($_13) {
  return $_13.mcp.add;
}
function _temp18($) {
  return $.menuAriaLabel;
}
function _temp19($_0) {
  return $_0.reconnect;
}
function _temp20($_1) {
  return $_1.replaceHeader;
}